import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthorizationManager } from '@hooks/authorization';
import useCmsApi from '@hooks/api/useCmsApi';
import { useStudioContext } from '@hooks/studioContext';
import {
  clampAnimatedThumbnailStart,
  getAnimatedThumbnailAspectRatio,
  getAnimatedThumbnailMaxStart,
  getAnimatedThumbnailResource,
  getAnimatedThumbnailSaveDisabledReason,
  getAnimatedThumbnailShape,
  getAnimatedThumbnailSourceApi,
  isAnimatedThumbnailProcessing,
  resolveAnimatedThumbnailEditableSource,
  resolveAnimatedThumbnailSource,
} from './animatedThumbnailContract';

const useAnimatedThumbnailEditor = () => {
  const cmsApi = useCmsApi();
  const studioContext = useStudioContext();
  const { authContext } = useAuthorizationManager();
  const { assetType, resourceId } = studioContext;
  const resource = getAnimatedThumbnailResource(assetType);
  const shape = getAnimatedThumbnailShape(assetType);
  const sourceApi = useMemo(
    () => getAnimatedThumbnailSourceApi(cmsApi, assetType),
    [assetType, cmsApi]
  );
  const [reloadVersion, setReloadVersion] = useState(0);
  const [loadState, setLoadState] = useState({
    animatedThumbnail: null,
    error: null,
    isLoading: true,
    source: null,
  });
  const [duration, setDuration] = useState(null);
  const [sourceDimensions, setSourceDimensions] = useState(null);
  const [sourceMetadataError, setSourceMetadataError] = useState(null);
  const [startSeconds, setStartSeconds] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    let isCurrent = true;
    setLoadState({
      animatedThumbnail: null,
      error: null,
      isLoading: true,
      source: null,
    });
    setDuration(null);
    setSourceDimensions(null);
    setSourceMetadataError(null);
    setStartSeconds(0);
    setSaveError(null);

    if (!sourceApi || !resource || !resourceId) {
      setLoadState({
        animatedThumbnail: null,
        error: new Error('Animated thumbnail context is invalid.'),
        isLoading: false,
        source: null,
      });
      return () => {
        isCurrent = false;
      };
    }

    Promise.all([
      sourceApi.get(resourceId).then(
        (response) => ({ response }),
        (error) => ({ error })
      ),
      shape
        ? cmsApi.animatedThumbnailApi.get(resource, resourceId, shape)
        : cmsApi.animatedThumbnailApi.get(resource, resourceId),
    ])
      .then(([sourceResult, animatedThumbnailResponse]) => {
        if (!isCurrent) {
          return;
        }

        const animatedThumbnail = animatedThumbnailResponse.payload;
        const editableSource =
          resolveAnimatedThumbnailEditableSource(animatedThumbnail);

        if (!editableSource && sourceResult.error) {
          throw sourceResult.error;
        }

        setStartSeconds(editableSource?.startSeconds ?? 0);
        setLoadState({
          animatedThumbnail,
          error: null,
          isLoading: false,
          source:
            editableSource ||
            resolveAnimatedThumbnailSource(
              assetType,
              sourceResult.response?.payload
            ),
        });
      })
      .catch((error) => {
        if (!isCurrent) {
          return;
        }

        setLoadState({
          animatedThumbnail: null,
          error,
          isLoading: false,
          source: null,
        });
      });

    return () => {
      isCurrent = false;
    };
  }, [
    assetType,
    cmsApi,
    reloadVersion,
    resource,
    resourceId,
    shape,
    sourceApi,
  ]);

  const setVideoDuration = useCallback((nextDuration) => {
    const normalizedDuration =
      Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : null;
    setDuration(normalizedDuration);
    setStartSeconds((currentStart) =>
      clampAnimatedThumbnailStart(currentStart, normalizedDuration)
    );
  }, []);

  useEffect(() => {
    if (!loadState.source?.url) {
      return undefined;
    }

    const video = document.createElement('video');
    let isCurrent = true;
    const handleLoadedMetadata = () => {
      if (!isCurrent) {
        return;
      }

      setVideoDuration(video.duration);

      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setSourceDimensions({
          width: video.videoWidth,
          height: video.videoHeight,
        });
        setSourceMetadataError(null);
      } else {
        setSourceMetadataError(
          new Error('The source video dimensions could not be determined.')
        );
      }
    };
    const handleError = () => {
      if (isCurrent) {
        setSourceMetadataError(
          new Error('The source video metadata could not be loaded.')
        );
      }
    };

    video.preload = 'metadata';
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('error', handleError);
    video.src = loadState.source.url;

    return () => {
      isCurrent = false;
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
      video.removeAttribute('src');
    };
  }, [loadState.source?.url, setVideoDuration]);

  const source = useMemo(
    () =>
      loadState.source && sourceDimensions
        ? { ...loadState.source, ...sourceDimensions }
        : loadState.source,
    [loadState.source, sourceDimensions]
  );
  const loadError = loadState.error || sourceMetadataError;
  const isSourceMetadataLoading = Boolean(
    loadState.source?.url && !sourceDimensions && !sourceMetadataError
  );

  const selectStartSeconds = useCallback(
    (nextStart) => {
      setStartSeconds(clampAnimatedThumbnailStart(nextStart, duration));
    },
    [duration]
  );

  const isProcessing = isAnimatedThumbnailProcessing(
    loadState.animatedThumbnail
  );
  const saveDisabledReason = getAnimatedThumbnailSaveDisabledReason({
    duration,
    hasLoadError: Boolean(loadError),
    isLoading: loadState.isLoading,
    isProcessing,
    isSaving,
    source,
  });

  const generate = useCallback(
    async (composition) => {
      if (!composition?.studioDefinition || !composition?.page) {
        throw new Error(
          'The complete animated-thumbnail Studio composition is required.'
        );
      }

      if (
        saveInFlightRef.current ||
        saveDisabledReason ||
        !resource ||
        !resourceId
      ) {
        throw new Error(
          saveDisabledReason || 'Animated thumbnail generation is unavailable.'
        );
      }

      saveInFlightRef.current = true;
      const clampedStart = clampAnimatedThumbnailStart(startSeconds, duration);
      setStartSeconds(clampedStart);
      setIsSaving(true);
      setSaveError(null);

      try {
        if (source?.sourceId) {
          if (shape) {
            await cmsApi.animatedThumbnailApi.generate(
              resource,
              resourceId,
              clampedStart,
              source.sourceId,
              shape,
              composition
            );
          } else {
            await cmsApi.animatedThumbnailApi.generate(
              resource,
              resourceId,
              clampedStart,
              source.sourceId,
              undefined,
              composition
            );
          }
        } else if (shape) {
          await cmsApi.animatedThumbnailApi.generate(
            resource,
            resourceId,
            clampedStart,
            undefined,
            shape,
            composition
          );
        } else {
          await cmsApi.animatedThumbnailApi.generate(
            resource,
            resourceId,
            clampedStart,
            undefined,
            undefined,
            composition
          );
        }
        return true;
      } catch (error) {
        setSaveError(error);
        throw error;
      } finally {
        saveInFlightRef.current = false;
        setIsSaving(false);
      }
    },
    [
      cmsApi,
      duration,
      resource,
      resourceId,
      saveDisabledReason,
      shape,
      source,
      startSeconds,
    ]
  );

  const save = useCallback(
    async (composition) => {
      if (saveDisabledReason) {
        return false;
      }

      try {
        await generate(composition);
        authContext.redirectToCmsEditForm(studioContext);
        return true;
      } catch (_error) {
        return false;
      }
    },
    [authContext, generate, saveDisabledReason, studioContext]
  );

  const discard = useCallback(() => {
    authContext.redirectToCmsAfterDiscardingChanges(studioContext);
  }, [authContext, studioContext]);

  const retry = useCallback(
    () => setReloadVersion((currentVersion) => currentVersion + 1),
    []
  );

  return {
    animatedThumbnail: loadState.animatedThumbnail,
    aspectRatio: getAnimatedThumbnailAspectRatio(assetType),
    discard,
    duration,
    generate,
    isLoading: loadState.isLoading,
    isSourceMetadataLoading,
    isProcessing,
    isSaving,
    loadError,
    maxStartSeconds: getAnimatedThumbnailMaxStart(duration),
    retry,
    save,
    saveDisabledReason,
    saveError,
    selectStartSeconds,
    setVideoDuration,
    source,
    studioDefinition: loadState.animatedThumbnail?.studioDefinition || null,
    startSeconds,
  };
};

export default useAnimatedThumbnailEditor;
